import React from 'react';
import _ from 'lodash';
import Joi from 'joi';
import JoiPhoneNumber from 'joi-phone-number';
import PropTypes from 'prop-types';

import Form from '../common/form';
import Progress from '../themed/progress';
import withCountries from '../hoc/withCountries';

import * as service from '../../services/organizationService';
import * as toastService from '../../services/toastService';

import * as utils from '../../utils/utilities';
import * as models from '../../utils/models';

class UpdateOrganization extends Form {
	state = {
		data: {
			organisationName: '',
			organisationWebsite: '',
			organisationLogo: [],
			organisationEmail: '',
			organisationPhone: '',
			street: '',
			city: '',
			state: '',
			country: '',
		},
		errors: {},
		loading: false,
		progress: 0,
	};

	schema = {
		organisationName: Joi.string().min(3).required().label('Organization Name'),
		organisationWebsite: Joi.string()
			.min(3)
			.required()
			.label('Organization Url'),
		organisationEmail: Joi.string()
			.email({ tlds: false })
			.required()
			.label('Contact Email'),
		organisationPhone: Joi.extend(JoiPhoneNumber)
			.string()
			.phoneNumber({ defaultCountry: 'NG', strict: true })
			.label('Contact Phone number'),
		organisationLogo: Joi.array().min(0).required().label('Organization Logo'),
		street: Joi.string().min(3).required().label('Street'),
		city: Joi.string().min(3).required().label('City'),
		state: Joi.string().min(3).required().label('State'),
		country: Joi.string().min(3).required().label('Country'),
	};

	static propTypes = {
		organization: PropTypes.object,
	};

	fetchStates = async (): Promise<void> => {
		const { country } = this.state.data;
		this.props.getStatesInCountry(country);
	};

	fetchCitiesInState = async (): Promise<void> => {
		const { country, state } = this.state.data;
		if (!country || !state) return;

		this.props.getCitiesInState(country, state);
	};

	handleStateChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
		await this.handleChange(e);

		this.fetchCitiesInState();
	};

	handleCountryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
		await this.handleChange(e);

		const { country } = this.state.data;
		this.props.getStatesInCountry(country);
	};

	resetOrganization = (): void => {
		const organization: models.Organization | null = this.props.organization;
		if (!organization) return;

		this.setState({
			data: {
				organisationName: organization.organisationName,
				organisationWebsite: organization.organisationWebsite,
				organisationLogo: [
					{
						refNo: 1,
						format: 'jpg',
						src: organization.organisationLogo,
						caption: organization.organisationName,
					},
				],
				organisationEmail: organization.organisationEmail,
				organisationPhone: organization.organisationPhone,
				street: organization.organisationAddress.street,
				city: organization.organisationAddress.city,
				state: organization.organisationAddress.state,
				country: organization.organisationAddress.country,
			},
		});
	};

	async componentDidMount(): Promise<void> {
		await this.resetOrganization();
		await this.fetchStates();
		await this.fetchCitiesInState();
	}

	getApiData = () => {
		const { data } = this.state;
		const apiData: any = {};

		apiData.organisationEmail = data.organisationEmail;
		apiData.organisationPhone = data.organisationPhone;
		apiData.organisationName = data.organisationName;
		apiData.organisationWebsite = data.organisationWebsite;
		apiData.organisationAddress = _.pick(data, [
			'street',
			'city',
			'state',
			'country',
		]);

		const logo: models.PDFImageProps[] = data.organisationLogo;
		if (logo.length > 0) apiData.organisationLogo = logo[0].src;

		return apiData;
	};

	postData = async (): Promise<void> => {
		const organization = this.props.organization;
		if (!organization) return;

		await service.editOrganization(organization._id, this.getApiData(), this.handleProgressChange);
		window.location.reload();
	};

	doSubmit = (): void => {
		toastService.promise(this.postData(), {
			loading: 'Updating Details',
			success: 'Details Updated',
			error: utils.getErrorMessage,
		});
	};

	render() {
		return (
			<React.Fragment>
				<Progress progress={this.state.progress} />

				<form autoComplete="off" onSubmit={this.handleSubmit} className="p-3">
					<h1 className="form-title">Update Organization Details</h1>

					<div className="divider my-4"></div>

					{this.renderFileInput({
						max: 1,
						multiple: false,
						name: 'organisationLogo',
						accept: 'image/*,application/pdf',
						label: 'Organization Logo',
						sublabel: 'Upload Organization Logo',
						types: utils.getFileUploadTypes(),
					})}

					<div className="divider my-4"></div>

					<div className="row mt-2">
						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'organisationName',
								label: 'Organization Name',
								placeholder: 'e.g. ExxonMobil',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'url',
								name: 'organisationWebsite',
								label: 'Organisation Url',
								placeholder: 'e.g. https://russelsmithgroup.com/',
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="row mt-2">
						<div className="col">
							{this.renderInput({
								type: 'email',
								name: 'organisationEmail',
								label: 'Email Address',
								placeholder: 'e.g. jdoe@russelsmithgroup.com',
							})}
						</div>

						<div className="col">
							{this.renderInput({
								type: 'text',
								name: 'organisationPhone',
								label: 'Phone Number',
								placeholder: 'e.g. 08142317489',
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					{this.renderInput({
						type: 'text',
						name: 'street',
						label: 'Street Address',
						placeholder: 'Enter Street Address',
					})}

					<div className="divider my-4"></div>

					<div className="row mt-2">
						<div className="col">
							{this.renderSelect({
								name: 'country',
								label: 'Country',
								placeholder: 'Select Country',
								options: this.props.countries,
								onChange: this.handleCountryChange,
							})}
						</div>
						<div className="col">
							{this.renderSelect({
								name: 'state',
								label: 'State',
								placeholder: 'Select State',
								options: this.props.states,
								onChange: this.handleStateChange,
							})}
						</div>
						<div className="col">
							{this.renderSelect({
								name: 'city',
								label: 'City',
								placeholder: 'Select City',
								options: this.props.cities,
							})}
						</div>
					</div>

					<div className="divider my-4"></div>

					<div className="d-flex justify-content-end">
						{this.renderButton('Update')}
					</div>
				</form>
			</React.Fragment>
		);
	}
}

export default withCountries(UpdateOrganization);
